import * as React from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Clock, Power, Ruler, Settings, ShoppingBag, Users } from 'lucide-react';
import { useSessao } from '@/hooks/useSessao';
import { Acessos } from '@/componentes/admin/Acessos';
import { PortaPalete } from '@/componentes/admin/PortaPalete';
import { MeliGalpoes } from '@/componentes/admin/MeliGalpoes';
import { Tarefas } from '@/componentes/admin/Tarefas';
import { Funcoes } from '@/componentes/admin/Funcoes';
import { Segmentado } from '@/componentes/ui/segmentado';
import { CabecalhoPagina } from '@/componentes/ui/primitivos';

/**
 * Administração: as duas coisas que o PBS não guarda.
 *
 * **Centros** é a medida da vaga do porta-palete e quais estoques contam
 * posição. **Mercado Livre** é de qual galpão sai cada conta — o que o painel
 * de expedição usa para saber de quem é cada pedido. **Acessos** é quem entra
 * no WMS e enxergando o quê; a base de usuários continua sendo a do ERP, aqui
 * só se libera. **Tarefas** é o cron do WMS: rotinas internas e URLs chamadas
 * no horário. **Funções** liga e desliga, sem deploy, o que está em teste.
 */
export function Admin(): JSX.Element {
  const { usuario } = useSessao();
  // Abre em "Centros": mexer em acesso é raro e deliberado, medir vaga é o que
  // se vem fazer aqui no dia a dia.
  const [params] = useSearchParams();
  const pedida = params.get('secao');
  const [secao, definirSecao] = React.useState<'centros' | 'meli' | 'acessos' | 'tarefas' | 'funcoes'>(
    pedida === 'meli' || pedida === 'acessos' || pedida === 'tarefas' || pedida === 'funcoes'
      ? pedida
      : 'centros',
  );

  // Rota protegida também no roteador; aqui é a segunda barreira.
  if (usuario && !usuario.adminMaster) return <Navigate to="/" replace />;

  return (
    <div className="space-y-4 p-3 sm:p-5">
      <CabecalhoPagina
        icone={<Settings className="h-5 w-5" />}
        sobrelinha="Configuração"
        titulo="Administração"
        descricao="Parâmetros do CD que o PBS não guarda, e quem pode entrar no WMS."
        acoes={
          <Segmentado
            valor={secao}
            aoMudar={definirSecao}
            rotuloAcessivel="Área da administração"
            opcoes={[
              {
                valor: 'centros',
                rotulo: 'Centros de estoque',
                icone: <Ruler className="h-4 w-4" />,
              },
              {
                valor: 'meli',
                rotulo: 'Mercado Livre',
                icone: <ShoppingBag className="h-4 w-4" />,
              },
              { valor: 'acessos', rotulo: 'Acessos', icone: <Users className="h-4 w-4" /> },
              { valor: 'tarefas', rotulo: 'Tarefas', icone: <Clock className="h-4 w-4" /> },
              { valor: 'funcoes', rotulo: 'Funções', icone: <Power className="h-4 w-4" /> },
            ]}
          />
        }
      />

      {secao === 'centros' ? <PortaPalete /> : null}

      {secao === 'meli' ? <MeliGalpoes /> : null}

      {secao === 'acessos' ? <Acessos /> : null}

      {secao === 'tarefas' ? <Tarefas /> : null}

      {secao === 'funcoes' ? <Funcoes /> : null}
    </div>
  );
}
